import { alpha, makeStyles } from "@material-ui/core";

/**
 * Flex-based layout matching coolremote-client UI.
 *
 * Items:  [left badge] [midSection (name + info row)] [rightSection (alerts + switch)]
 * Groups: [arrow]       [innerContainer (left + sensorInfo + right)]
 */

const useStyles = makeStyles((theme: any) => ({

  // ─── Container Base ───────────────────────────────────────────────────────

  row: {
    width: "100%",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    color: theme.palette.colors.controlGroupToggleColor || "#fff",
    marginBottom: 6,
    boxSizing: "border-box",
    position: "relative",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "& .MuiSwitch-root": {
      padding: 0,
      margin: 0,
    },
  },
  itemRow: {
    background: alpha("#fff", 0.1),
    padding: "8px 16px",
    minHeight: 60,
  },
  groupRow: {
    background: theme.palette.colors.controlGroupToggleBg || alpha("#fff", 0.2),
    padding: "8px 16px",
    height: 60,
  },

  // ─── State Modifiers ──────────────────────────────────────────────────────

  selected: { border: "solid 2px rgba(255, 255, 255, 0.6)" },
  erroredDiv: {
    backgroundImage:
      "repeating-linear-gradient(-45deg, rgba(139, 50, 44, 0.26), rgba(139, 50, 44, 0.26) 24px, transparent 24px, transparent 48px)",
  },
  dimmedItem: { color: alpha("#fff", 0.6) },
  boldText: { fontWeight: 600 },

  // ═══ UNIT: Status Circle ═══════════════════════════════════════════════════

  statusBox: {
    margin: "0 12px 0 0",
    height: 44,
    width: 44,
    minWidth: 44,
    borderRadius: "50%",
    backgroundColor: "#5c4f5f",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: 17,
    fontWeight: 400,
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
  },
  statusPowerOn: {
    backgroundColor: "#5c4f5f",
  },
  statusHeat: {
    backgroundColor: "#f05146 !important",
  },
  statusCool: {
    backgroundColor: "#35a8e0 !important",
  },
  erroredStatusBox: {
    backgroundColor: alpha("#5c4f5f", 0.4),
  },
  statusBoxTextSm: {
    fontSize: 14,
  },
  statusBoxTextXs: {
    fontSize: 13,
  },
  errorMsg: {
    fontSize: 14,
    fontWeight: 400,
    color: "#F05146",
  },

  // ═══ SENSOR: Icon Box ══════════════════════════════════════════════════════

  iconBox: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: 8,
    backgroundColor: alpha("#fff", 0.2),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative" as const,
    marginRight: 16,
    flexShrink: 0,
  },

  // ═══ MID SECTION — shared base (always column) ═══════════════════════════

  midSection: {
    display: "flex",
    flex: 1,
    flexDirection: "column" as const,
    overflow: "hidden",
    gap:20,
  },

  // ─── Unit: row modifier (midUpper + midLower on same line) ──────────────
  midSectionRow: {
    flexDirection: "row" as const,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "nowrap" as const,
    overflow: "hidden",
    fontWeight: 700 as const,
    "@media (max-width: 768px)": {
      flexDirection: "column" as const,
      alignItems: "flex-start",
      gap: 4,
    },
  },
  midUpper: {
    flexShrink: 0,
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    "@media (max-width: 768px)": {
      flex: "none",
      width: "100%",
    },
  },
  midBottomSection: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flex: 1,
    "@media (max-width: 768px)": {
      flex: "none",
      width: "100%",
    },
  },
  midLower: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexShrink: 0,
    "@media (min-width: 769px)": {
      marginLeft: "auto",
    },
  },
  midLowerItem: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minWidth: 20,
  },
  currTemp: {
    fontSize: 14,
    fontFamily: "Roboto",
    fontWeight: 400,
    color: alpha(theme.palette.colors.controlGroupToggleColor || "#fff", 0.6),
    width: "clamp(80px, 10vw, 120px)",
    minWidth: 80,
    maxWidth: 120,
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
    "& *": {
      fontFamily: "Roboto",
      fontWeight: 400,
    },
    "@media (max-width: 768px)": {
      textAlign: "left" as const,
      width: "auto",
      minWidth: "unset",
      maxWidth: "unset",
    },
  },

  // ─── Sensor: always stacked, gap between items ─────────────────────────
  sensorBottomRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },

  // ═══ NAME ══════════════════════════════════════════════════════════════════

  nameText: {
    fontSize: 14,
    fontWeight: 400,
    width: "clamp(200px, 15vw, 300px)",
    minWidth: 120,
    maxWidth: 200,
  },
  nameTextLg: {
    fontSize: 18,
  },
  // Group names can be long (e.g. "Ungrouped BACnet Devices"), so they use the row's free space instead of the unit-name cap
  groupName: {
    width: "auto",
    maxWidth: "100%",
    minWidth: 0,
  },

  // ═══ RIGHT SECTION (alerts + switch) ═══════════════════════════════════════

  rightSection: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginLeft: 12,
    gap: 16,
  },
  importantNoteSec: {
    display: "flex",
    height: "100%",
    justifyContent: "space-between",
    alignItems: "center",
  },

  // ═══ SENSOR VALUE ══════════════════════════════════════════════════════════

  sensorValue: {
    fontSize: 14,
    fontWeight: 400,
    color: alpha(theme.palette.colors.controlGroupToggleColor || "#fff", 0.5),
  },
  specialFont: { fontFamily: "-webkit-body", fontSize: 16 },

  // ═══ POWER METER ═══════════════════════════════════════════════════════════

  nameSection: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    overflow: "hidden",
    marginRight: 16,
  },
  powerValue: {
    fontSize: 26,
    fontWeight: 300,
    fontFamily: "RobotoLight",
    display: "flex",
    alignItems: "baseline",
  },
  powerUnit: {
    fontSize: 16,
    marginLeft: 4,
  },

  // ═══ DISCONNECTED ══════════════════════════════════════════════════════════

  disconnectedIcon: {
    width: 28,
    height: 28,
    fontSize: 28,
  },
  disconnectedContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  disconnectedInfo: {
    display: "flex",
    alignItems: "center",
    gap: '3px',
    marginLeft: "12px",
    fontSize: "14px",
    fontWeight: 400,
    color: '#F05146 !important',
  },
  transformSVG: {
    transform: 'translateY(0.5px)',
  },

  // ═══ INDICATORS ════════════════════════════════════════════════════════════

  noteIcon: { fontSize: 20 },
  alertIcon: { width: 20, height: 20 },
  errorContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.colors.carminePink || "#F05146",
  },

  // ═══ GROUP ═════════════════════════════════════════════════════════════════

  iconHolder: {
    width: 24,
    height: 24,
    padding: 0,
  },
  innerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    height: "100%",
    overflow: "hidden",
  },
  leftSection: {
    display: "flex",
    flex: 1,
    minWidth: 0,
    flexDirection: "column" as const,
    justifyContent: "center",
    overflow: "hidden",
    gap: 2,
  },
  topRow: {
    overflow: "hidden",
  },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  groupInfo: {
    fontSize: 14,
    fontWeight: 400,
    color: alpha(theme.palette.colors.controlGroupToggleColor || "#fff", 0.5),
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
  scheduleIcon: {
    width: 22,
    height: 22,
    color: alpha(theme.palette.colors.controlGroupToggleColor || "#fff", 0.5),
  },
  sensorGroupInfoSection: {
    display: "flex",
    flexDirection: "column" as const,
    flex: 1,
  },
  sensorGroupInfoLine: {
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap" as const,
    "& p": { fontSize: 14 },
  },
  emptySection: {
    width: "36px",
  }
}));

export default useStyles;
